import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import * as FaIcons from "react-icons/fa";

import {
  FaHome,
  FaBoxOpen,
  FaChevronDown,
  FaTools,
  FaImages,
  FaInfoCircle,
  FaPhoneAlt,
  FaBars,
  FaTimes,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaArrowRight,
  FaLayerGroup,
} from "react-icons/fa";

import { BASE_URL } from "../../Config/config";
import "./Navbar.css";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  /* =====================================================
     CONTACT STATE
  ===================================================== */

  const [contactData, setContactData] = useState({
    phone: "+91 9824393921",
    email: "info@tallyworkshop.co.in",
  });

  /* =====================================================
     PRODUCTS STATE
  ===================================================== */

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  /* =====================================================
     FETCH CONTACT DETAILS
  ===================================================== */

  const fetchContactDetails = async () => {
    try {
      const response = await fetch(`${BASE_URL}/contact.php`);

      if (!response.ok) {
        throw new Error(`Contact API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid Contact API Response:", text);

        return;
      }

      console.log("NAVBAR CONTACT API:", result);

      if (!result.success) {
        console.error(result.message || "Failed to fetch contact details.");

        return;
      }

      const data = result.data || {};

      setContactData({
        phone: data.phone,

        email: data.email,
      });
    } catch (error) {
      console.error("Fetch Contact Details Error:", error);
    }
  };

  /* =====================================================
     FETCH PRODUCTS
  ===================================================== */

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);

      const response = await fetch(`${BASE_URL}/products.php?action=get`);

      if (!response.ok) {
        throw new Error(`Products API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid Products API Response:", text);

        setProducts([]);
        return;
      }

      console.log("NAVBAR PRODUCTS API:", result);

      if (!result.status) {
        console.error(result.message || "Failed to fetch products.");

        setProducts([]);
        return;
      }

      const apiProducts = Array.isArray(result.data) ? result.data : [];

      /* -----------------------------------------------
         ONLY ACTIVE PRODUCTS
      ----------------------------------------------- */

      const activeProducts = apiProducts
        .filter((item) => Boolean(Number(item.status)))
        .map((item) => ({
          ...item,

          name: item.name || "",

          description:
            item.description || "Complete business software solution.",

          short_description:
            item.short_description ||
            item.description ||
            "Business software solution.",

          slug:
            item.slug ||
            String(item.name || "")
              .toLowerCase()
              .trim()
              .replace(/&/g, "and")
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, ""),

          icon: item.icon || "FaBoxOpen",
        }));

      setProducts(activeProducts);
    } catch (error) {
      console.error("Fetch Products Error:", error);

      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  /* =====================================================
     LOAD DATA
  ===================================================== */

  useEffect(() => {
    fetchContactDetails();
    fetchProducts();
  }, []);

  /* =====================================================
     NAVBAR SHOW / HIDE ON SCROLL
  ===================================================== */

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Always show navbar at top
      if (currentScrollY <= 10) {
        setShowNavbar(true);
      }

      // Scrolling down
      else if (currentScrollY > lastScrollY) {
        setShowNavbar(false);
      }

      // Scrolling up
      else if (currentScrollY < lastScrollY) {
        setShowNavbar(true);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* =====================================================
     CLOSE MENU
  ===================================================== */

  const closeMenu = () => {
    setMobileOpen(false);
    setProductsOpen(false);
  };

  /* =====================================================
     TOGGLE PRODUCTS
  ===================================================== */

  const toggleProducts = () => {
    setProductsOpen((prev) => !prev);
  };

  /* =====================================================
     ACTIVE ROUTE
  ===================================================== */

  const isActive = (path) => {
    return location.pathname === path;
  };

  /* =====================================================
     PRODUCTS ACTIVE
  ===================================================== */

  const isProductsActive = () => {
    return location.pathname.startsWith("/products");
  };

  /* =====================================================
     PRODUCT SUBMENU ACTIVE
  ===================================================== */

  const isProductSubmenuActive = (slug) => {
    return location.pathname === `/products/${slug}`;
  };

  /* =====================================================
     PRODUCT ICON
  ===================================================== */

  const DEFAULT_PRODUCT_ICON = "FaBoxOpen";

  const getProductIcon = (icon) => {
    const iconName = String(icon || DEFAULT_PRODUCT_ICON).trim();

    const IconComponent = FaIcons[iconName] || FaIcons[DEFAULT_PRODUCT_ICON];

    return <IconComponent />;
  };

  /* =====================================================
     PRODUCT SLUG HELPER
  ===================================================== */

  const getProductSlug = (product) => {
    if (product.slug) {
      return product.slug;
    }

    return String(product.name || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  /* =====================================================
     GO TO PRODUCTS PAGE
  ===================================================== */

  const goToProducts = () => {
    closeMenu();
    navigate("/products");
  };

  /* =====================================================
     PHONE URL
  ===================================================== */

  const phoneNumber = contactData.phone || "";

  const phoneUrl = `tel:${phoneNumber.replace(/[^\d+]/g, "")}`;

  /* =====================================================
     EMAIL URL
  ===================================================== */

  const emailAddress = contactData.email || "";

  const emailUrl = `mailto:${emailAddress}`;

  /* =====================================================
     RETURN
  ===================================================== */

  return (
    <header
      className={`main-header ${
        showNavbar ? "navbar-visible" : "navbar-hidden"
      }`}
    >
      {/* =====================================================
          TOP CONTACT BAR
      ===================================================== */}

      <div className="top-bar">
        <div className="top-bar-container">
          <div className="top-contact">
            {/* PHONE */}

            <a href={phoneUrl}>
              <FaPhoneAlt />

              <span>{contactData.phone}</span>
            </a>

            {/* EMAIL */}

            <a href={emailUrl}>
              <FaEnvelope />

              <span>{contactData.email}</span>
            </a>
          </div>

          <div className="top-message">
            <span>Need a business software solution?</span>

            <Link to="/contact">Talk to our experts</Link>
          </div>
        </div>
      </div>

      {/* =====================================================
          BRAND SECTION
      ===================================================== */}

      <div className="brand-section">
        <div className="brand-container">
          {/* LOGO */}

          <Link to="/home" className="logo-area" onClick={closeMenu}>
            <img
              src="/favicon.ico"
              alt="Star Computers & Information Technology"
              className="logo"
            />

            <div className="brand-text">
              <h1>Star Computers & Information Technology</h1>

              <p>Best Service , Right Time , Right People</p>
            </div>
          </Link>

          {/* =================================================
              NAV CONTAINER
          ================================================= */}

          <div className="nav-container">
            {/* MOBILE BUTTON */}

            <button
              type="button"
              className="mobile-toggle"
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>

            {/* =================================================
                NAVIGATION
            ================================================= */}

            <ul className={`nav-menu ${mobileOpen ? "active" : ""}`}>
              {/* HOME */}

              <li>
                <Link
                  to="/home"
                  className={isActive("/home") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaHome />

                  <span>Home</span>
                </Link>
              </li>

              {/* =================================================
                  PRODUCTS DROPDOWN
              ================================================= */}

              <li
                className="dropdown"
                onMouseEnter={() => {
                  if (window.innerWidth > 768) {
                    setProductsOpen(true);
                  }
                }}
                onMouseLeave={() => {
                  if (window.innerWidth > 768) {
                    setProductsOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  className={`dropdown-btn ${
                    isProductsActive() ? "active" : ""
                  }`}
                  onClick={() => {
                    if (window.innerWidth <= 768) {
                      toggleProducts();
                    } else {
                      goToProducts();
                    }
                  }}
                  aria-expanded={productsOpen}
                >
                  <FaBoxOpen />

                  <span>Products</span>

                  <FaChevronDown
                    className={`dropdown-arrow ${productsOpen ? "rotate" : ""}`}
                  />
                </button>

                {/* =================================================
                    PRODUCT DROPDOWN
                ================================================= */}

                <ul className={`dropdown-menu ${productsOpen ? "show" : ""}`}>
                  {productsLoading ? (
                    <li className="product-loading">Loading products...</li>
                  ) : products.length === 0 ? (
                    <li className="product-empty">No products available</li>
                  ) : (
                    products.map((product, index) => {
                      const slug = getProductSlug(product);

                      return (
                        <li key={product.id || `product-${index}`}>
                          <Link
                            to={`/products/${slug}`}
                            className={
                              isProductSubmenuActive(slug) ? "active" : ""
                            }
                            onClick={closeMenu}
                          >
                            <div className="product-menu-icon">
                              {getProductIcon(product.icon)}
                            </div>

                            <div>
                              <strong>{product.name}</strong>

                              <small>
                                {product.short_description ||
                                  product.description ||
                                  "Business software solution"}
                              </small>
                            </div>
                          </Link>
                        </li>
                      );
                    })
                  )}
                </ul>
              </li>

              {/* TALLY MODULES */}

              <li>
                <Link
                  to="/modules"
                  className={isActive("/modules") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaLayerGroup />

                  <span>Tally Modules</span>
                </Link>
              </li>

              {/* SERVICES */}

              <li>
                <Link
                  to="/services"
                  className={isActive("/services") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaTools />

                  <span>Services</span>
                </Link>
              </li>

              {/* GALLERY */}

              <li>
                <Link
                  to="/gallery"
                  className={isActive("/gallery") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaImages />

                  <span>Gallery</span>
                </Link>
              </li>

              {/* ABOUT */}

              <li>
                <Link
                  to="/about"
                  className={isActive("/about") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaInfoCircle />

                  <span>About Us</span>
                </Link>
              </li>

              {/* CONTACT */}

              <li>
                <Link
                  to="/contact"
                  className={isActive("/contact") ? "active" : ""}
                  onClick={closeMenu}
                >
                  <FaPhoneAlt />

                  <span>Contact</span>
                </Link>
              </li>

              {/* =================================================
                  MOBILE CONTACT INFO
              ================================================= */}

              <li className="mobile-contact-info">
                <a href={phoneUrl}>
                  <FaPhoneAlt />

                  <span>{contactData.phone}</span>
                </a>

                <a href={emailUrl}>
                  <FaEnvelope />

                  <span>{contactData.email}</span>
                </a>
              </li>

              {/* =================================================
                  MOBILE QUOTE
              ================================================= */}

              <li className="mobile-quote-wrapper">
                <Link
                  to="/contact"
                  className="mobile-quote"
                  onClick={closeMenu}
                >
                  <FaFileInvoiceDollar />

                  <span>Get a Quote</span>

                  <FaArrowRight />
                </Link>
              </li>
            </ul>
          </div>

          {/* =================================================
              HEADER RIGHT
          ================================================= */}

          <div className="header-right">
            <Link to="/contact" className="quote-btn" onClick={closeMenu}>
              <FaFileInvoiceDollar />

              <span>Get a Quote</span>

              <FaArrowRight className="quote-arrow" />
            </Link>
          </div>
        </div>
      </div>

      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <nav className="navbar"></nav>
    </header>
  );
}
